// @ts-nocheck

import { useMemo } from 'react';
import { Dialog, Form, useDialogContext, Button as AsioButton } from '@rich-products-corp/primitives-richproducts';
import { useToast } from '@rich-products-corp/react-ecomm-ui/dist';
import { AddressForm } from '@rich-products-corp/react-ecomm-ui/dist/components/PageSections/Ecommerce/forms/address';
import { addressFormSchema } from '@rich-products-corp/react-ecomm-ui/dist/components/PageSections/Ecommerce/forms/schemas/address.schema';
import { cn } from '@rich-products-corp/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { genGenIDNumber } from './utils';
import Spinner from '@/components/commercetools-ui/spinner';
import { useUpdateAddress } from '@/hooks/my-account/address/use-update-address';
import { useGetAccount } from '@/hooks/my-account/use-get-account';
import useDeviceDetect from '@/hooks/useDeviceDetect';
import useScreenOrientation from '@/hooks/useOrientation';
import { useGoogleAutocomplete } from '@/hooks/useGoogleAutocomplete';
import { useAddressValidation } from '@/hooks/useAddressValidation';

export type UpdateAddressDialogProps = {
  addressId?: string;
  states: any[];
};

const schema = yup
  .object()
  .shape({
    addressFormValues: addressFormSchema, //MONKEYPATCH: @Variaban - this is the only way to get this to work. Please check this before merging.
  })
  .required();

type AddressFormSchemaType = yup.InferType<typeof schema>;

export const UpdateAddressDialog = ({ addressId, states, ...props }: UpdateAddressDialogProps) => {
  const { validateAddress, DialogComponent } = useAddressValidation();
  const { data } = useGetAccount();
  const { mutateAsync: updateAddress, isLoading: isUpdateAddressLoading } = useUpdateAddress();
  const [, { close }] = useDialogContext();
  const { toast } = useToast();
  const { isMobile } = useDeviceDetect();
  const orientation = useScreenOrientation();

  const addressToEdit: any = useMemo(() => {
    if (data && data?.addresses?.length > 0) {
      return data.addresses.find((address) => address.addressId === addressId);
    } else {
      return null;
    }
  }, [data, addressId]);

  const form = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      addressFormValues: {
        ...addressToEdit,
        companyName: addressToEdit?.company,
        streetAddress: addressToEdit?.streetName,
        zipCode: addressToEdit?.postalCode,
        apartment: addressToEdit?.additionalStreetInfo,
      },
    },
  });

  // Google Address Autocomplete.
  useGoogleAutocomplete({
      selector: 'input[name="addressFormValues.streetAddress"]',
      form,
  });

  const onSubmit = async (values: AddressFormSchemaType) => {
    const addressId = await genGenIDNumber('ADDRESS', toast);
    const { addressFormValues } = values;

    // ADDRESS VALIDATION START
    const isValid = await validateAddress([
    {
        addressSequence: 'EDIT_1',
        firstName: addressFormValues.firstName,
        streetName: addressFormValues.streetAddress,
        postalCode: addressFormValues.zipCode,
        city: addressFormValues.city,
        state: addressFormValues.state,
        country: 'US',
        company: addressFormValues.company || '',
        phone: addressFormValues.phone || '',
        additionalAddressInfo: addressFormValues.apartment || '',
    }
    ]);

    if (!isValid) {
        return; // STOP if user clicked NO
    }
    // ADDRESS VALIDATION END

    const { streetAddress: streetName, zipCode: postalCode, apartment, ...formValues } = addressFormValues;

    const responseUpdateAddress = await updateAddress({
      ...formValues,
      streetName,
      streetNumber: '',
      postalCode,
      country: 'US',
      additionalStreetInfo: apartment,
      city: addressFormValues.city,
      state: addressFormValues.state,
      firstName: addressFormValues.firstName,
      lastName: addressFormValues.lastName,
      key: addressId.toString() ?? undefined,
    });

    if (!responseUpdateAddress.isError) {
      close();
    } else {
      toast({
        title: 'Unable to update address at this time, Please try again!',
        duration: 5000,
      });
    }
  };

  return (
    <Dialog {...props}>
      <Dialog.Content
          onInteractOutside={(e) => {
            const target = e.target as HTMLElement;

            // Allow Google autocomplete clicks
            if (target.closest('.pac-container')) {
              e.preventDefault();
              return;
            }
          }}
          className={cn(
            orientation === 'landscape-primary' && isMobile
              ? 'top-0 flex h-full flex-initial flex-col overflow-scroll'
              : 'h-full max-h-fit min-w-fit md:h-auto',
          )}
        >
        <Dialog.Header className="my-6 flex flex-row justify-center">
          <Dialog.Title className="text-3xl leading-6">Edit Recipient/Address</Dialog.Title>
        </Dialog.Header>
        {!addressToEdit ? (
          <Spinner />
        ) : (
          <Form
            onSubmit={form.handleSubmit(onSubmit)}
            actions={
              <nav className="flex flex-row justify-center">
                <AsioButton type="button" variant="link" onClick={close} className="mr-2 py-6">
                  Cancel
                </AsioButton>
                <AsioButton loading={isUpdateAddressLoading} type="submit" className="bg-gray-900 text-white">
                  {isUpdateAddressLoading ? 'submitting...' : 'SAVE CHANGES'}
                </AsioButton>
              </nav>
            }
          >
            <AddressForm form={form} schemaKey="addressFormValues" states={states} title="" />
          </Form>
        )}
        {/* REQUIRED */}
        <DialogComponent />
      </Dialog.Content>
    </Dialog>
  );
};
